import { com } from '@eidu/web'
import { Stack, Typography } from '@mui/material'
import EntityWithLabelAndRelated from '../../domain/entity/EntityWithLabelAndRelated'
import { findEntityTypeByFieldId, findFieldById } from '../../util/EntityTypeExtensions'
import fieldValueLabel from '../../util/FieldValueExtensions'
import { fieldValueToString } from '../../domain/entity/DraftFieldValue'
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import EntityId = com.eidu.sharedlib.entity.EntityId
import EntitySelector = com.eidu.sharedlib.entity.selector.EntitySelector

export type SelectorHeaderLabel = { primary: string; secondary?: string }

export const formatSelectorLabelText = (
  selector: EntitySelector,
  entityTypes: readonly EntityType[],
  entitiesByTypeId: ReadonlyMap<EntityTypeId, ReadonlyMap<EntityId, EntityWithLabelAndRelated>>
): string => {
  if (EntitySelector.All.equals(selector)) return 'All'
  if (selector instanceof EntitySelector.OfType) {
    const typeName = entityTypes.find((type) => type.id.equals(selector.typeId))?.name
    return typeName ? String(typeName) : `Unknown type (${selector.typeId})`
  }
  if (selector instanceof EntitySelector.Single) {
    const typeName = entityTypes.find((type) => type.id.equals(selector.typeId))?.name
    const entityLabel =
      entitiesByTypeId.get(selector.typeId)?.get(selector.entityId)?.primaryLabelText ?? selector.entityId.toString()
    return `${typeName ? String(typeName) : `Unknown type (${selector.typeId})`}:${entityLabel}`
  }
  if (selector instanceof EntitySelector.FieldValueEquals) {
    const entityType = findEntityTypeByFieldId(entityTypes, selector.fieldId)
    if (!entityType) return `Unknown type (${selector.fieldId})`
    const field = findFieldById(entityType, selector.fieldId)
    if (!field) return String(entityType.name)
    const rawValue = fieldValueToString(selector.value)
    const label = fieldValueLabel(field, selector.value, entitiesByTypeId) ?? rawValue
    return `${entityType.name}:${label}(${field.name})`
  }
  if (selector instanceof EntitySelector.And)
    return [...selector.selectors.asJsReadonlyArrayView()]
      .map((item) => formatSelectorLabelText(item, entityTypes, entitiesByTypeId))
      .join(' & ')
  if (selector instanceof EntitySelector.Or)
    return [...selector.selectors.asJsReadonlyArrayView()]
      .map((item) => formatSelectorLabelText(item, entityTypes, entitiesByTypeId))
      .join(' / ')
  if (selector instanceof EntitySelector.Not)
    return `NOT ${formatSelectorLabelText(selector.selector, entityTypes, entitiesByTypeId)}`
  return selector.toString()
}

export const formatSelectorHeaderLabel = (
  selector: EntitySelector,
  entityTypes: readonly EntityType[],
  entitiesByTypeId: ReadonlyMap<EntityTypeId, ReadonlyMap<EntityId, EntityWithLabelAndRelated>>
): SelectorHeaderLabel => {
  if (EntitySelector.All.equals(selector)) return { primary: 'All Users' }
  if (selector instanceof EntitySelector.OfType) {
    const typeName = entityTypes.find((type) => type.id.equals(selector.typeId))?.name
    return { primary: typeName ? String(typeName) : `Unknown type (${selector.typeId})`, secondary: 'All' }
  }
  if (selector instanceof EntitySelector.FieldValueEquals) {
    const entityType = findEntityTypeByFieldId(entityTypes, selector.fieldId)
    if (!entityType) return { primary: `Unknown type (${selector.fieldId})` }
    const field = findFieldById(entityType, selector.fieldId)
    if (!field) return { primary: String(entityType.name) }
    const rawValue = fieldValueToString(selector.value)
    const label = fieldValueLabel(field, selector.value, entitiesByTypeId) ?? rawValue
    return { primary: String(entityType.name), secondary: `${field.name}: ${label}` }
  }
  if (selector instanceof EntitySelector.FieldValueEqualsUserEntityId) {
    const entityType = findEntityTypeByFieldId(entityTypes, selector.fieldId)
    if (!entityType) return { primary: `Unknown type (${selector.fieldId})` }
    const field = findFieldById(entityType, selector.fieldId)
    if (!field) return { primary: String(entityType.name) }
    return { primary: String(entityType.name), secondary: `${field.name}: User` }
  }
  if (selector instanceof EntitySelector.FieldValueEqualsUserFieldValue) {
    const entityType = findEntityTypeByFieldId(entityTypes, selector.fieldId)
    if (!entityType) return { primary: `Unknown type (${selector.fieldId})` }
    const field = findFieldById(entityType, selector.fieldId)
    if (!field) return { primary: String(entityType.name) }
    const userEntityType = findEntityTypeByFieldId(entityTypes, selector.userFieldId)
    const userField = userEntityType ? findFieldById(userEntityType, selector.userFieldId) : undefined
    const userFieldLabel =
      userField && userEntityType ? `${userEntityType.name}.${userField.name}` : selector.userFieldId.toString()
    return { primary: String(entityType.name), secondary: `${field.name}: ${userFieldLabel}` }
  }
  return { primary: formatSelectorLabelText(selector, entityTypes, entitiesByTypeId) }
}

export const SelectorHeaderLabelView = ({ label }: { label: SelectorHeaderLabel }) => (
  <Stack spacing={0} alignItems="flex-start" textAlign="left">
    <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
      {label.primary}
    </Typography>
    {label.secondary ? (
      <Typography variant="caption" sx={{ fontWeight: 400, lineHeight: 1.2 }}>
        {label.secondary}
      </Typography>
    ) : null}
  </Stack>
)
